import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PageHeader from '../../components/PageHeader';
import useDebounce from '../../hooks/use-debounce';
import { stream_data } from '../../config/stream_data';
import { Modal, Button, Form } from "react-bootstrap";
import Swal from 'sweetalert2';

const InstituteTPOImportStudentList = () => {
  // API Configuration
  const API_BASE_URL = process.env.REACT_APP_API_LMS;
  const API_TOKEN = process.env.REACT_APP_API_TOKEN;
  const INSTITUTE_ID = sessionStorage.getItem("instituteId");

  // State Management
  const [students, setStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [studentSearchInput, setStudentSearchInput] = useState('');
  const [emailSearchInput, setEmailSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [error, setError] = useState(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Filter States
  const [selectedDegree, setSelectedDegree] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedStudyingYear, setSelectedStudyingYear] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [emailFilter, setEmailFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // update password
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [selectedStudentupdate, setSelectedStudentUpdate] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [email, setEmail] = useState("");
  const [loadingPwd, setLoadingPwd] = useState(false);
  const [errorPwd, setErrorPwd] = useState("");

  // Debounced search values
  const debouncedStudentSearch = useDebounce(studentSearchInput, 500);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Fetch students for dropdown based on search (minimum 3 characters)
  useEffect(() => {
    const searchTerm = studentSearchInput || emailSearchInput;
    if (searchTerm.length >= 3) {
      fetchStudentsForDropdown(searchTerm);
    } else {
      setAllStudents([]);
    }
  }, [debouncedStudentSearch, emailSearchInput]);

  const fetchStudentsForDropdown = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 3) {
      setAllStudents([]);
      return;
    }

    setLoadingStudents(true);
    try {
      const queryParams = new URLSearchParams({
        institute_id: INSTITUTE_ID,
        search: searchTerm
      });

      const payload = {
        institute_id: parseInt(INSTITUTE_ID),
        search: searchTerm
      };

      const response = await fetch(`${API_BASE_URL}/api/students-by-institute`, {
        method: 'POST',
        headers: {
          'Authorization': API_TOKEN,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const result = await response.json();

      if (result.status === 'success') {
        setAllStudents(result.data);
      } else {
        console.error('Failed to fetch students');
        setAllStudents([]);
      }
    } catch (err) {
      console.error('Student fetch error:', err);
      setAllStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  // Fetch students when filters change
  useEffect(() => {
    fetchStudents();
  }, [selectedDegree, selectedYear, selectedStudyingYear, selectedStudent, emailFilter, searchQuery, currentPage, perPage]);

  // Fetch and filter students based on query params
  const fetchStudents = async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        institute_id: parseInt(INSTITUTE_ID),
        page: currentPage,
        per_page: perPage
      });

      if (selectedDegree) {
        queryParams.append('degree_title', selectedDegree.value);
      }

      if (selectedYear) {
        queryParams.append('year', selectedYear.getFullYear());
      }

      if (selectedStudyingYear) {
        queryParams.append('studying_year', selectedStudyingYear.value);
      }

      if (emailFilter) {
        queryParams.append('email', emailFilter);
      }

      if (searchQuery) {
        queryParams.append('search', searchQuery);
      }

      console.log('Fetching students with params:', queryParams.toString());

      const response = await fetch(`${API_BASE_URL}/api/students-details?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': API_TOKEN,
          'Accept': 'application/json'
        }
      });

      const result = await response.json();

      if (result.status) {
        setStudents(result.data);
        setTotalPages(result.pagination?.last_page || 1);
        setTotalRecords(result.pagination?.total || result.data.length);
      } else {
        setError('Failed to fetch student details');
      }
    } catch (err) {
      setError('Failed to fetch student details');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Reset all filters
  const handleClearFilters = () => {
    setSelectedDegree(null);
    setSelectedYear(new Date());
    setSelectedStudyingYear(null);
    setSelectedStudent(null);
    setEmailFilter('');
    setSearchQuery('');
    setStudentSearchInput('');
    setEmailSearchInput('');
    setCurrentPage(1);
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Prepare options for react-select using stream_data
  const degreeOptions = useMemo(() => {
    const degrees = stream_data.streams.flatMap(stream =>
      stream.degrees.map(degree => degree.name)
    );
    return [...new Set(degrees)].map(degree => ({
      value: degree,
      label: degree
    }));
  }, []);

  const studyingYearOptions = useMemo(() => {
    return stream_data.studying_years.map(year => ({
      value: year,
      label: year
    }));
  }, []);

  const studentOptions = useMemo(() => {
    return allStudents.map(student => ({
      value: student.id,
      label: `${student.first_name} ${student.last_name}`,
      email: student.email
    }));
  }, [allStudents]);

  const emailOptions = useMemo(() => {
    const uniqueEmails = [...new Set(allStudents.map(student => student.email))];
    return uniqueEmails.filter(email => email).map(email => ({
      value: email,
      label: email
    }));
  }, [allStudents]);

  // React-Select custom styles
  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '38px',
      borderColor: '#dee2e6'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    })
  };

  // Wrapper component for DatePicker to match react-select styling
  const YearPickerWrapper = ({ value, onChange, placeholder }) => (
    <div className="position-relative">
      <DatePicker
        selected={value}
        onChange={onChange}
        showYearPicker
        dateFormat="yyyy"
        yearItemNumber={9}
        placeholderText={placeholder}
        className="form-control"
        isClearable
        wrapperClassName="d-block"
        maxDate={new Date()}
      />
    </div>
  );

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (selectedDegree) count++;
    if (selectedYear) count++;
    if (selectedStudyingYear) count++;
    if (selectedStudent) count++;
    if (emailFilter) count++;
    if (searchQuery) count++;
    return count;
  };

  // Filter Panel Component
  const FilterPanel = () => (
    <>
      {/* Backdrop */}
      {showFilterPanel && (
        <div
          className="offcanvas-backdrop fade show"
          onClick={() => setShowFilterPanel(false)}
          style={{ zIndex: 1040 }}
        ></div>
      )}

      {/* Side Panel */}
      <div
        className={`offcanvas offcanvas-end ${showFilterPanel ? 'show' : ''}`}
        tabIndex="-1"
        style={{
          visibility: showFilterPanel ? 'visible' : 'hidden',
          width: '320px',
          zIndex: 1045
        }}
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="offcanvas-title">
            <i className="fas fa-filter me-2"></i>Filters
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setShowFilterPanel(false)}
          ></button>
        </div>
        <div className="offcanvas-body">
          <div className="mb-3">
            <label className="form-label fw-semibold">Degree Title</label>
            <Select
              options={degreeOptions}
              value={selectedDegree}
              onChange={setSelectedDegree}
              placeholder="Select degree..."
              isClearable
              styles={selectStyles}
            />
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold">Year</label>
            <YearPickerWrapper
              value={selectedYear}
              onChange={setSelectedYear}
              placeholder="Select year..."
            />
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold">Studying Year</label>
            <Select
              options={studyingYearOptions}
              value={selectedStudyingYear}
              onChange={setSelectedStudyingYear}
              placeholder="Select studying year..."
              isClearable
              styles={selectStyles}
            />
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold">
              Student
            </label>
            <Select
              options={studentOptions}
              value={selectedStudent}
              onInputChange={(value) => setStudentSearchInput(value)}
              onChange={(option) => {
                setSelectedStudent(option);
                if (option) {
                  setSearchQuery(`${option.label}`);
                  setEmailFilter(option.email);
                } else {
                  setSearchQuery('');
                  setEmailFilter('');
                }
              }}
              placeholder="Type to search student..."
              isClearable
              isLoading={loadingStudents}
              noOptionsMessage={() =>
                studentSearchInput.length < 3
                  ? "Type at least 3 characters"
                  : "No students found"
              }
              styles={selectStyles}
            />
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold">
              Email
            </label>
            <Select
              options={emailOptions}
              value={emailOptions.find(option => option.value === emailFilter)}
              onInputChange={(value) => setEmailSearchInput(value)}
              onChange={(option) => setEmailFilter(option ? option.value : '')}
              placeholder="Type to search email..."
              isClearable
              isLoading={loadingStudents}
              noOptionsMessage={() =>
                emailSearchInput.length < 3
                  ? "Type at least 3 characters"
                  : "No emails found"
              }
              styles={selectStyles}
            />
          </div>

          {/* <div className="mb-3">
            <label className="form-label fw-semibold">Search</label>
            <input 
              type="text" 
              className="form-control"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name..."
            />
          </div> */}

          <div className="d-grid gap-2">
            <button
              className="btn btn-primary"
              onClick={() => {
                setShowFilterPanel(false);
              }}
            >
              <i className="fas fa-check me-2"></i>Apply Filters
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => {
                handleClearFilters();
                setShowFilterPanel(false);
              }}
            >
              <i className="fas fa-redo me-2"></i>Reset Filters
            </button>
          </div>
        </div>
      </div>
    </>
  );

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleOpenPasswordModal = (student) => {
    setSelectedStudentUpdate(student);
    setEmail(student.email || "");
    setNewPassword("");
    setErrorPwd("");
    setShowPwdModal(true);
  };

  const handleClosePasswordModal = () => {
    setShowPwdModal(false);
    setSelectedStudentUpdate(null);
  }

  const handleUpdatePassword = async () => {
    if (!newPassword.trim()) {
      setErrorPwd("Password is required");
      return;
    }

    if (!isValidEmail(email)) {
      setErrorPwd("Please enter a valid email address");
      return;
    }

    if (newPassword.length < 8) {
      setErrorPwd("Password must be at least 8 characters long");
      return;
    }

    setLoadingPwd(true);
    setErrorPwd("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/student/update-credentials`, {
        method: "POST",
        headers: {
          Authorization: API_TOKEN,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          institute_id: INSTITUTE_ID,
          student_id: selectedStudentupdate.id,
          email: email,
          password: newPassword,
        })
      });

      const result = await response.json();
      console.log(result);

      if (!response.ok || result.status === false) {
        const errorMsg = result.errors
          ? Object.values(result.errors).flat().join(", ")
          : (result.message || "Failed to update password");
        throw new Error(errorMsg);
      }

      Swal.fire({
        icon: "success",
        title: "Password Updated",
        text: "Student password has been updated successfully",
        confirmButtonColor: "#198754",
      });
      await fetchStudents();
      handleClosePasswordModal();
    } catch (err) {
      setErrorPwd(err.message);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: err.message || "Something went wrong",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoadingPwd(false);
    }
  };


  return (
    <>
      {/* Header */}
      <div className="row">
        <div className="col-12">
          <PageHeader
            title="Student List"
            breadcrumbs={[
              { label: "Dashboard", path: "/institute-dashboard" },
              { label: "Student List", active: true }
            ]}
          />
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {/* Mobile Filter Button */}
      <div className="d-md-none mb-3">
        <div className="d-flex gap-2">
          <button
            className="btn btn-primary flex-grow-1 position-relative"
            onClick={() => setShowFilterPanel(true)}
          >
            <i className="fas fa-filter me-2"></i>Filters
            {getActiveFilterCount() > 0 && (
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                {getActiveFilterCount()}
              </span>
            )}
          </button>
          {getActiveFilterCount() > 0 && (
            <button
              className="btn btn-outline-secondary"
              onClick={handleClearFilters}
              title="Reset Filters"
            >
              <i className="fas fa-redo me-2"></i>Reset
            </button>
          )}
        </div>
      </div>

      {/* Desktop Filters Section */}
      <div className="card shadow-sm mb-4 d-none d-md-block">
        <div className="card-body">
          <div className="row g-3 mb-3">
            {/* Degree Title */}
            <div className="col-md-3">
              <label className="form-label fw-semibold">Degree Title</label>
              <Select
                options={degreeOptions}
                value={selectedDegree}
                onChange={setSelectedDegree}
                placeholder="Select degree..."
                isClearable
                styles={selectStyles}
              />
            </div>

            {/* Year */}
            <div className="col-md-3">
              <label className="form-label fw-semibold">Year</label>
              <YearPickerWrapper
                value={selectedYear}
                onChange={setSelectedYear}
                placeholder="Select year..."
              />
            </div>

            {/* Studying Year */}
            <div className="col-md-3">
              <label className="form-label fw-semibold">Studying Year</label>
              <Select
                options={studyingYearOptions}
                value={selectedStudyingYear}
                onChange={setSelectedStudyingYear}
                placeholder="Select studying year..."
                isClearable
                styles={selectStyles}
              />
            </div>

            {/* Student Name */}
            <div className="col-md-3">
              <label className="form-label fw-semibold">
                Student
              </label>
              <Select
                options={studentOptions}
                value={selectedStudent}
                onInputChange={(value) => setStudentSearchInput(value)}
                onChange={(option) => {
                  setSelectedStudent(option);
                  if (option) {
                    // setSearchQuery(`${option.label}`);
                    setEmailFilter(option.email);
                  } else {
                    // setSearchQuery('');
                    setEmailFilter('');
                  }
                }}
                placeholder="Type to search..."
                isClearable
                isLoading={loadingStudents}
                noOptionsMessage={() =>
                  studentSearchInput.length < 3
                    ? "Type at least 3 characters"
                    : "No students found"
                }
                styles={selectStyles}
              />
            </div>
          </div>

          <div className="row g-3">
            {/* Email */}
            <div className="col-md-4">
              <label className="form-label fw-semibold">
                Email
              </label>
              <Select
                options={emailOptions}
                value={emailOptions.find(option => option.value === emailFilter)}
                onInputChange={(value) => setEmailSearchInput(value)}
                onChange={(option) => setEmailFilter(option ? option.value : '')}
                placeholder="Type to search..."
                isClearable
                isLoading={loadingStudents}
                noOptionsMessage={() =>
                  emailSearchInput.length < 3
                    ? "Type at least 3 characters"
                    : "No emails found"
                }
                styles={selectStyles}
              />
            </div>

            {/* Search */}
            {/* <div className="col-md-4">
              <label className="form-label fw-semibold">Search</label>
              <input 
                type="text" 
                className="form-control"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name..."
              />
            </div> */}

            {/* Reset Button */}
            <div className="col-md-8 d-flex align-items-end justify-content-end">
              <button
                className="btn btn-primary"
                onClick={handleClearFilters}
              >
                <i className="fas fa-redo me-2"></i>Reset Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Panel for Mobile */}
      <FilterPanel />

      {/* Table Section */}
      <div className="card skyblueborder">
        <div className="card-header skyblue-header">
          <div className="row align-items-center">
            {/* Title on its own row on mobile, inline on desktop */}
            <div className="col-12 col-md-auto mb-2 mb-md-0">
              <h5 className="mb-0 text-primary">
                <i className="fas fa-users me-2"></i>
                Student Records ({totalRecords})
              </h5>
            </div>
            {/* Per-page selector on its own row on mobile, inline on desktop */}
            <div className="col-12 col-md-auto ms-md-auto">
              <div className="d-flex align-items-center">
                <label className="me-2 mb-0 text-nowrap">Show per page:</label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                  value={perPage}
                  onChange={handlePerPageChange}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover table-striped mb-0">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th className='tableNoWrap'>First Name</th>
                  <th className='tableNoWrap'>Last Name</th>
                  <th>Email</th>
                  <th>Contact</th>
                  <th>Stream</th>
                  <th>Degree</th>
                  {/* <th>Specialization</th> */}
                  <th>Year</th>
                  <th className='tableNoWrap'>Studying Year</th>
                  <th>Division</th>
                  <th className='text-center'>Action</th>
                  {/* <th>Gender</th>
                  <th>DOB</th> */}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="13" className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 mb-0">Loading student data...</p>
                    </td>
                  </tr>
                ) : students.length > 0 ? (
                  students.map((student) => (
                    <tr key={student.id}>
                      <td>{student.id}</td>
                      <td>{student.first_name}</td>
                      <td>{student.last_name}</td>
                      <td className="text-break tableNoWrap" style={{ minWidth: '200px' }}>{student.email}</td>
                      <td className="text-nowrap">{student.contact_number || 'N/A'}</td>
                      <td>{student.stream || 'N/A'}</td>
                      <td className="text-nowrap">{student.degree_title || 'N/A'}</td>
                      {/* <td>{student.specialization || 'N/A'}</td> */}
                      <td>{student.year || 'N/A'}</td>
                      <td className="text-nowrap">{student.studying_year || 'N/A'}</td>
                      <td>{student.division || 'N/A'}</td>
                      <th><button className='btn btn-sm btn-outline-danger' onClick={() => handleOpenPasswordModal(student)}>Update Password</button></th>
                      {/* <td>
                        <span className={`badge ${student.gender === 'male' ? 'bg-info' : 'bg-warning'}`}>
                          {student.gender || 'N/A'}
                        </span>
                      </td>
                      <td className="text-nowrap">{formatDate(student.date_of_birth || 'N/A')}</td> */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="13" className="text-center py-5 text-muted">
                      <i className="fas fa-search me-2"></i>
                      No students found matching your filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {!loading && students.length > 0 && (
          <div className="card-footer bg-white">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
              <div className="text-muted text-center text-md-start">
                Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalRecords)} of {totalRecords} entries
              </div>

              <nav>
                <ul className="pagination mb-0 flex-wrap justify-content-center">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                  </li>

                  {getPageNumbers().map((page, index) => (
                    <li
                      key={index}
                      className={`page-item ${page === currentPage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}
                    >
                      {page === '...' ? (
                        <span className="page-link">...</span>
                      ) : (
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      )}
                    </li>
                  ))}

                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}
      </div>
      <Modal show={showPwdModal} onHide={handleClosePasswordModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Update Password</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedStudentupdate && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Student Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter new password"
                  value={`${selectedStudentupdate?.first_name} ${selectedStudentupdate?.last_name}`}
                  disabled
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  isInvalid={!!errorPwd && errorPwd.toLowerCase().includes("email")}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>New Password</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (e.target.value.length < 8) {
                      setErrorPwd("Password must be at least 8 characters long");
                    } else {
                      setErrorPwd("");
                    }
                  }}
                  // onChange={(e) => setNewPassword(e.target.value)}
                  isInvalid={!!errorPwd}
                />
                <Form.Control.Feedback type="invalid">
                  {errorPwd}
                </Form.Control.Feedback>
              </Form.Group>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant='outline-danger' onClick={handleClosePasswordModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdatePassword}
            disabled={loadingPwd}
          >
            {loadingPwd ? "Updating..." : "Update Password"}
          </Button>
        </Modal.Footer>
      </Modal>

    </>
  );
};

export default InstituteTPOImportStudentList;